/**
 * Sparkline — a 24-hour connectivity bar, one cell per hour.
 *
 * Answers "has this been STABLE?", which a status dot cannot. On 2026-09-10 three devices
 * were "up" by one measure and dead by another, and separating them took hours of reading
 * gateway logs by hand; a flapping row and a steady row are one glance apart here.
 *
 * THE DESIGN RULE THAT MATTERS: a cell with no data is NOT a cell that is down.
 *
 *   up      solid healthy      we have evidence the device was talking in that hour
 *   gap     solid critical     silence BETWEEN two confirmed contacts — a real outage
 *   null    faint hatch        no evidence either way
 *
 * The null treatment is deliberately a low-contrast *texture* rather than a colour on the
 * status ramp, because "unknown" is not a point on the healthy/critical axis at all. An AP
 * onboarded two hours ago has 22 empty cells; painting those red would report an outage that
 * never happened, and an indicator that is wrong on day one is not believed on day ninety.
 *
 * Rendered as inline SVG rather than divs: one element per row instead of 24, which matters
 * when this is a column in a list of several hundred devices.
 */
import { useId } from "react";

import type { ConnectivityBucket } from "@/types/domain";

interface SparklineProps {
  /** Oldest-first. Phase-aligned across devices by the API, so columns are comparable. */
  buckets: ConnectivityBucket[] | undefined;
  /** Rollup for the accessible label — the numbers, not the picture. */
  summary?: { up: number; gap: number; observed: number; stable: boolean };
  className?: string;
}

// Cell geometry in the SVG's own user units; CSS scales the whole thing.
const CELL_W = 4;
const GAP_W = 1;
const HEIGHT = 16;

export function Sparkline({ buckets, summary, className }: SparklineProps) {
  // A unique pattern id PER INSTANCE. An SVG fragment id is document-global, so a hardcoded
  // one would collide across every row of a device list and all of them would resolve to
  // whichever instance mounted first -- fine until that row unmounts, at which point the rest
  // silently lose their fill.
  const patternId = `md-spark-unknown-${useId()}`;

  if (!buckets || buckets.length === 0) {
    return <span className="text-ink-subtle">—</span>;
  }

  const width = buckets.length * (CELL_W + GAP_W) - GAP_W;
  const label = describe(buckets, summary);

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${width} ${HEIGHT}`}
      // Fixed height, width scales with the cell count. max-w-full keeps it inside a narrow
      // table cell rather than forcing the row wider.
      // text-ink-subtle lives HERE, on the root, not on the individual rects: `currentColor`
      // inside a <pattern> resolves against the pattern content's own inherited color, not
      // against the element that references it. Colouring the rect would leave the hatch
      // painted with the default (black) instead of the muted ink.
      className={`text-ink-subtle ${className ?? "h-4 w-full max-w-[140px]"}`}
      preserveAspectRatio="none"
    >
      <defs>
        {/* Hatch for "no data". A texture, not a ramp colour — see the header. currentColor
            picks up the root's text-ink-subtle, so it tracks both themes with no extra
            tokens. */}
        <pattern
          id={patternId}
          width="3"
          height="3"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <line x1="0" y1="0" x2="0" y2="3" stroke="currentColor" strokeWidth="1" opacity="0.28" />
        </pattern>
      </defs>
      {buckets.map((b, i) => (
        <rect
          key={i}
          x={i * (CELL_W + GAP_W)}
          y={0}
          width={CELL_W}
          height={HEIGHT}
          rx={1}
          className={b === "up" ? "fill-healthy" : b === "gap" ? "fill-critical" : undefined}
          fill={b === "up" || b === "gap" ? undefined : `url(#${patternId})`}
          // "Up" is deliberately MUTED and "gap" is at full strength, because visual weight
          // should track how much something needs attention. Rendered at equal strength a
          // healthy fleet is a wall of saturated green — the loudest thing on the page is
          // "nothing to see here", and the one device with an outage is a small mark inside
          // it. Quieting the normal case is what makes the abnormal one findable in
          // peripheral vision down a list of several hundred rows.
          fillOpacity={b === "up" ? 0.5 : 1}
        />
      ))}
    </svg>
  );
}

/**
 * The accessible label. Says what the picture says, in the same words the rest of the UI
 * uses — and is careful not to call an unobserved device "stable".
 */
function describe(
  buckets: ConnectivityBucket[],
  summary?: { up: number; gap: number; observed: number; stable: boolean },
): string {
  const up = summary?.up ?? buckets.filter((b) => b === "up").length;
  const gap = summary?.gap ?? buckets.filter((b) => b === "gap").length;
  const observed = summary?.observed ?? up + gap;
  const hours = buckets.length;

  if (observed === 0) {
    return `No connectivity data for the last ${hours} hours.`;
  }
  if (gap === 0) {
    return observed === hours
      ? `Connected for all ${hours} of the last ${hours} hours.`
      : `Connected for all ${observed} observed hours; no data for the other ${hours - observed}.`;
  }
  return `${gap} hour${gap === 1 ? "" : "s"} without contact out of ${observed} observed, in the last ${hours} hours.`;
}
